import { AxiosError } from 'axios';
import type { ApiErrorDetail, ApiErrorResponse } from '@/types/api';

/**
 * Normalised client-side error. Every failed request is surfaced as one of these
 * so UI code has a stable shape (code, message, field details, correlationId) to
 * render — never a raw AxiosError (spec §30).
 */
export class ApiClientError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: ApiErrorDetail[];
  readonly correlationId?: string;
  /** True for connectivity failures (no HTTP response received). */
  readonly isNetworkError: boolean;

  constructor(params: {
    code: string;
    message: string;
    status: number;
    details?: ApiErrorDetail[];
    correlationId?: string;
    isNetworkError?: boolean;
  }) {
    super(params.message);
    this.name = 'ApiClientError';
    this.code = params.code;
    this.status = params.status;
    this.details = params.details ?? [];
    this.correlationId = params.correlationId;
    this.isNetworkError = params.isNetworkError ?? false;
  }

  /** Map backend field errors onto a `{ field: message }` object for RHF. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details) {
      if (detail.field) out[detail.field] = detail.message;
    }
    return out;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
  get isNotFound(): boolean {
    return this.status === 404;
  }
  get isConflict(): boolean {
    return this.status === 409;
  }
  get isRateLimited(): boolean {
    return this.status === 429;
  }
  get isValidation(): boolean {
    return this.code === 'VALIDATION_ERROR' || this.status === 422;
  }
  get isServerError(): boolean {
    return this.status >= 500;
  }
}

function isApiErrorResponse(value: unknown): value is ApiErrorResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { success?: unknown }).success === false &&
    typeof (value as { error?: unknown }).error === 'object'
  );
}

/** Convert any thrown value into an {@link ApiClientError}. */
export function normalizeError(error: unknown): ApiClientError {
  if (error instanceof ApiClientError) return error;

  if (error instanceof AxiosError) {
    const response = error.response;

    // No response => network / offline / timeout / CORS.
    if (!response) {
      const offline = typeof navigator !== 'undefined' && !navigator.onLine;
      return new ApiClientError({
        code: offline ? 'OFFLINE' : 'NETWORK_ERROR',
        message: offline
          ? 'You appear to be offline. Check your connection and try again.'
          : 'Could not reach the server. Please try again.',
        status: 0,
        isNetworkError: true,
      });
    }

    const body: unknown = response.data;
    const correlationId =
      (response.headers?.['x-correlation-id'] as string | undefined) ??
      (isApiErrorResponse(body) ? body.correlationId : undefined);

    if (isApiErrorResponse(body)) {
      return new ApiClientError({
        code: body.error.code,
        message: body.error.message,
        status: response.status,
        details: body.error.details,
        correlationId,
      });
    }

    return new ApiClientError({
      code: 'HTTP_ERROR',
      message: error.message || `Request failed with status ${response.status}`,
      status: response.status,
      correlationId,
    });
  }

  if (error instanceof Error) {
    return new ApiClientError({ code: 'UNKNOWN', message: error.message, status: 0 });
  }

  return new ApiClientError({
    code: 'UNKNOWN',
    message: 'An unexpected error occurred',
    status: 0,
  });
}
